import Phaser from "phaser";
import { generateProceduralTextures, proceduralFileEntries } from "@yjsgames/theme";
import { GameScene } from "./GameScene.js";
import { houseAdImagePreloads } from "../ads/houseAdData.js";
import { THEME } from "../themes/activeTheme.js";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
import { FONT_FAMILY, installSceneTextResolutionPatch, TYPO } from "../ui/uiTokens.js";
import { advanceLoadingPhase, loadingProgressToAngle, type LoadingPhase } from "./loadingState.js";

/**
 * 부트 = 로딩 씬 — 회전 인디케이터 + "Loading" 텍스트를 먼저 그린 뒤, 테마 매니페스트(@app-theme)
 * 기반 에셋을 동적 로드한다. 로드 완료 후 GameScene을 launch(병렬 기동)하고, GameScene이 저장 로드 +
 * buildUi를 끝내 EVENT_READY를 발화할 때까지 이 씬을 최상단 오버레이로 유지한다 — 그래야 "검은 화면 →
 * 구슬만 보이는 뷰" 중간 단계가 사용자에게 노출되지 않는다(준비 완료 후 fade out → stop).
 *
 * preload()를 비우고 create()에서 load.start()하는 이유: Phaser는 preload 완료 전 create를 호출하지
 * 않아 preload 동안엔 스피너를 그릴 수 없다. 로딩 UI를 먼저 그리려면 로드를 create로 옮겨야 한다.
 *
 * 텍스처 키·경로·도형 색은 전부 테마 데이터(src/themes/<name>/manifest.ts) 소유 — 스킨 교체 시 이
 * 파일은 수정하지 않는다. 색은 PALETTE 토큰만 사용한다(themeStatic 게이트가 hex 리터럴 0건 강제).
 */
export class BootScene extends Phaser.Scene {
  static readonly KEY = "Boot";

  /** 로딩 인디케이터 기하 — 화면 중앙 원형 스피너. */
  private static readonly SPINNER_RADIUS = 26;
  private static readonly SPINNER_THICKNESS = 5;
  private static readonly SPINNER_SPIN_MS = 1000;
  private static readonly FADE_MS = 200;

  private phase: LoadingPhase = "assetsLoading";
  private overlay?: Phaser.GameObjects.Container;
  private spinnerArc?: Phaser.GameObjects.Graphics;
  private spinnerTween?: Phaser.Tweens.Tween;
  /** GameScene 인스턴스 대기 재시도 타이머 — 씬 종료(SHUTDOWN) 시 취소해 파괴된 this 접근을 막는다. */
  private attachRetryTimer?: Phaser.Time.TimerEvent;

  constructor() {
    super(BootScene.KEY);
  }

  create(): void {
    this.phase = "assetsLoading";
    installSceneTextResolutionPatch(this);
    this.buildLoadingUi();
    this.load.on(Phaser.Loader.Events.PROGRESS, this.onLoadProgress, this);
    this.load.once(Phaser.Loader.Events.COMPLETE, this.onAssetsLoaded, this);
    this.load.on(Phaser.Loader.Events.FILE_LOAD_ERROR, this.onLoadError, this);
    // 종료(씬 stop/game destroy) 시 무한 회전 tween 정리 — 누락 시 destroy 중 deref NPE.
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.cleanup, this);
    this.queueAssets();
    this.load.start();
  }

  /**
   * 에셋 큐잉 — BootScene.preload()에 있던 로드를 create의 동적 로드로 이전. headless 스모크
   * 테스트(SmokeBootScene)가 이 메서드를 override해 실네트워크 XHR 대신 canvas 텍스처를 등록한다.
   */
  protected queueAssets(): void {
    for (const [key, path] of Object.entries(THEME.images)) {
      this.load.image(key, path);
    }
    // 절차 생성 대신 실 PNG로 대체된 텍스처 (매니페스트 kind: "file").
    for (const { key, path } of proceduralFileEntries(THEME.procedural)) {
      this.load.image(key, path);
    }
    // 하우스 광고 image 캠페인 — 데이터(houseAds.tsv) 기반 동적 preload (video는 DOM이라 제외).
    for (const preload of houseAdImagePreloads()) {
      this.load.image(preload.key, preload.url);
    }
  }

  /** 로딩 UI — 전면 입력차단 배경 + 트랙 원 + 진행 호(회전) + "Loading" 텍스트. */
  private buildLoadingUi(): void {
    const width = this.scale.width;
    const height = this.scale.height;
    const cx = width / 2;
    const cy = height / 2;
    const r = BootScene.SPINNER_RADIUS;

    // launch된 GameScene이 뒤에서 도는 동안 시각·터치를 가리는 전면 배경(입력 흡수).
    const blocker = this.add
      .rectangle(0, 0, width, height, PALETTE_NUM.appBackground)
      .setOrigin(0, 0)
      .setInteractive();

    // 트랙(전체 원) — 회전하지 않으므로 절대 좌표에 1회 그림.
    const track = this.add.graphics();
    track.lineStyle(BootScene.SPINNER_THICKNESS, PALETTE_NUM.panelDivider, 1);
    track.strokeCircle(cx, cy, r);

    // 진행 호 — 로컬 (0,0) 중심에 그리고 위치를 (cx,cy)로 둬야 setRotation이 제자리 회전이 된다
    // (절대 좌표에 그리면 회전축이 좌상단이라 호가 궤도를 돈다).
    this.spinnerArc = this.add.graphics({ x: cx, y: cy });
    this.drawArc(0);

    const label = this.add
      .text(cx, cy + r + 28, "Loading", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.body,
        color: PALETTE.textPrimary,
      })
      .setOrigin(0.5);

    this.overlay = this.add.container(0, 0, [blocker, track, this.spinnerArc, label]);

    this.spinnerTween = this.tweens.add({
      targets: this.spinnerArc,
      rotation: Math.PI * 2,
      duration: BootScene.SPINNER_SPIN_MS,
      repeat: -1,
    });
  }

  /** 진행 호를 progress(0~1)만큼의 sweep으로 다시 그림 — 회전(rotation)은 tween이 유지. */
  private drawArc(progress: number): void {
    const arc = this.spinnerArc;
    if (!arc) return;
    const sweep = loadingProgressToAngle(progress);
    arc.clear();
    if (sweep <= 0) return;
    const start = -Math.PI / 2; // 12시 방향에서 시작
    arc.lineStyle(BootScene.SPINNER_THICKNESS, PALETTE_NUM.accentGold, 1);
    arc.beginPath();
    arc.arc(0, 0, BootScene.SPINNER_RADIUS, start, start + sweep);
    arc.strokePath();
  }

  private onLoadProgress(progress: number): void {
    this.drawArc(progress);
  }

  private onLoadError(file: Phaser.Loader.File): void {
    // 부팅 초기라 diag-log 미초기화 가능 — console.warn(AutoPlay 캡처 형식)으로 실패 키만 남긴다.
    // Phaser는 실패 파일도 COMPLETE에 포함하므로 로드가 데드락되지 않는다.
    console.warn(`[BootScene] asset load error: ${file.key} (${file.url})`);
  }

  /** 에셋 로드 완료 → 절차 텍스처 생성 → GameScene 병렬 기동 + 최상단 유지 + 준비 신호 대기. */
  private onAssetsLoaded(): void {
    const next = advanceLoadingPhase(this.phase, "assets-complete");
    if (next === this.phase) return; // 이중 발화 방어
    this.phase = next;

    generateProceduralTextures(this, THEME.procedural);
    this.scene.launch(GameScene.KEY);
    this.scene.bringToTop(); // 로딩 오버레이를 GameScene 위에 유지
    this.attachGameReady();
  }

  /** GameScene EVENT_READY 구독 — 등록 전 이미 발화했을 race를 bootReady 선체크로 방어. */
  private attachGameReady(): void {
    const gameScene = this.scene.get(GameScene.KEY) as InstanceType<typeof GameScene> | null;
    if (!gameScene) {
      // 등록 인스턴스가 아직 없으면(이론상 드묾) 다음 틱 재시도 — 핸들을 보관해 SHUTDOWN 시 취소.
      this.attachRetryTimer = this.time.delayedCall(50, () => this.attachGameReady());
      return;
    }
    if (gameScene.bootReady) {
      this.onGameReady();
      return;
    }
    gameScene.events.once(GameScene.EVENT_READY, this.onGameReady, this);
  }

  /** GameScene 준비 완료 → 오버레이 fade out → 씬 종료(SHUTDOWN에서 tween 정리). */
  private onGameReady(): void {
    const next = advanceLoadingPhase(this.phase, "game-ready");
    if (next === this.phase) return; // 이중 발화 방어
    this.phase = next;

    this.spinnerTween?.stop();
    this.spinnerTween = undefined;
    if (!this.overlay) {
      this.scene.stop();
      return;
    }
    this.tweens.add({
      targets: this.overlay,
      alpha: 0,
      duration: BootScene.FADE_MS,
      onComplete: () => this.scene.stop(),
    });
  }

  private cleanup(): void {
    this.spinnerTween?.stop();
    this.spinnerTween = undefined;
    this.attachRetryTimer?.destroy();
    this.attachRetryTimer = undefined;
  }
}
